/* jscs:disable requireSpacesInsideJsxExpressionContainers */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { TrayTitle } from '@ux/tray-menu';
import { IntlProvider, FormattedMessage } from 'react-intl';
import Link from '@ux/link';
import eidBuilder from '../eid';
import ChevronRight from '@ux/icon/chevron-right';

/**
 * Render Quick Links section.
 *
 * @Constructor
 * @class QuickLinks
 * @api public
 */
class QuickLinks extends Component {
  /**
   * Build the items in a list
   *
   * @param {LinkItem[]} items An array of objects containing the details of a list item
   * @returns {React.ReactElement[]} An array of list items containing a link
   * @api private
   */
  listItemBuilder(items) {
    return items.map(item => {
      const captionOrId = item.caption || item.id;
      const caption = item.caption
        ? <FormattedMessage id={ item.caption } />
        : <span>{ item.title }</span>;

      return (
        <li key={ captionOrId }>
          <Link
            urlArgs={ this.props.urlArgs }
            href={ item.href }
            data-eid={ eidBuilder({
              key: captionOrId,
              manifest: this.props.manifest
            }) }>
            <span className='drop-right'>
              <ChevronRight/>
            </span>
            { caption }
          </Link>
        </li>
      );
    });
  }

  /**
   * @typedef {Object} LinkItem
   * @prop {LinkItem[]} [children] Array of objects that hold individual list items (caption, href)
   * @prop {String} [caption] Golf string
   * @prop {String} [title] Pre-translated text -- either caption or title must be set
   * @prop {String} [id] Identifier for item -- either caption or ID must be set
   * @prop {String} [href] Optional URL to link to
   */
  /**
   * Build a list
   *
   * @param {LinkItem[]} items An object containing the details for lists to build
   * @returns {Array} An array of lists
   * @api private
   */
  listBuilder(items) {
    if (!items) {
      return;
    }

    // https://github.secureserver.net/UX/eldorado/blob/master/application/includes/all-products-menu.jade#L14-L19
    const colCount = items.length;
    let colClassNum = 2;
    if (colCount === 5) {
      colClassNum = 15;
    } else if (colCount === 6 || colCount <= 4) {
      colClassNum = Math.floor(12 / colCount);
    }

    return items.map(item => {
      const caption = item.caption
        ? <FormattedMessage id={ item.caption } />
        : <span>{ item.title }</span>;

      return (
        <div
          key={ item.caption || item.id }
          className={ `col-md-${colClassNum}` }>
          <ul className='list-unstyled'>
            {(item.caption || item.title) && (
              <li>
                <strong>{ caption }</strong>
              </li>
            )}
            { this.listItemBuilder(item.children) }
          </ul>
        </div>
      );
    });
  }

  render() {
    const props = this.props;
    const utilityBarNavs = props.utilityBarNav || [];
    let quickLinks = {};

    for (let i = 0; i < utilityBarNavs.length; i++) {
      const nav = utilityBarNavs[i];
      const captionOrId = nav.caption || nav.id;
      if (captionOrId && ~captionOrId.indexOf('QuickLinks')) {
        quickLinks = nav;
        break;
      }
    }

    if ((!quickLinks.caption && !quickLinks.title) || !quickLinks.children) {
      return null;
    }

    const caption = quickLinks.caption
      ? <FormattedMessage id={ quickLinks.caption } />
      : <span>{ quickLinks.title }</span>;

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <div className='col-xl-5 pull-xl-7 col-lg-6 pull-lg-6 inbox-links'>
          <TrayTitle>
            { caption }
          </TrayTitle>
          <div className='row quick-links'>
            { this.listBuilder(quickLinks.children) }
          </div>
        </div>
      </IntlProvider>
    );
  }
}

/**
 * Require properties.
 *
 * @type {Object}
 * @api public
 */
QuickLinks.propTypes = {
  urls: PropTypes.object.isRequired,
  market: PropTypes.string.isRequired,
  messages: PropTypes.object.isRequired,
  utilityBarNav: PropTypes.arrayOf(PropTypes.object),
  manifest: PropTypes.string,
  urlArgs: PropTypes.object
};

export default QuickLinks;
